'use client';

import { Suspense, useState } from 'react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { AlertCircle, Eye, EyeOff, Lock, Mail, ShieldCheck, TrendingUp, Users } from 'lucide-react';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAuth } from '@/lib/AuthContext';

const FEATURES = [
  { icon: TrendingUp, text: 'Des milliers d’annonces actualisées chaque jour.' },
  { icon: ShieldCheck, text: 'Annonces vérifiées, transactions sécurisées.' },
  { icon: Users, text: 'Une communauté de confiance depuis 2010.' },
];

function LoginForm() {
  const { login } = useAuth();
  const router = useRouter();
  const searchParams = useSearchParams();
  const returnTo = searchParams.get('from') ?? searchParams.get('redirect') ?? '/';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      router.push(returnTo);
    } catch (err: unknown) {
      const e = err as { response?: { data?: { message?: string | string[] } }; message?: string };
      const msg = e?.response?.data?.message ?? e?.message ?? 'Identifiants incorrects.';
      setError(Array.isArray(msg) ? msg[0] : msg);
    } finally {
      setLoading(false);
    }
  };

  const registerHref = `/auth/register${returnTo && returnTo !== '/' ? `?from=${encodeURIComponent(returnTo)}` : ''}`;

  return (
    <AuthLayout
      eyebrow="Espace membre"
      headline={
        <>
          Bienvenue,
          <br />
          <span className="italic text-accent">ravi de vous revoir.</span>
        </>
      }
      description="La plateforme de référence pour acheter, vendre ou louer un bien immobilier au Maroc."
      features={FEATURES}
    >
      <header className="mb-8">
        <span className="eyebrow text-accent mb-3 inline-block">Connexion</span>
        <h1 className="font-display text-h2 text-ink leading-tight">Connectez-vous.</h1>
        <p className="text-ink-secondary mt-3 text-sm">
          Accédez à vos favoris, vos annonces et votre messagerie.
        </p>
      </header>

      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <div
            role="alert"
            className="flex items-start gap-2.5 rounded-xl border border-error/30 bg-error/5 px-4 py-3 text-sm text-error"
          >
            <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        <div>
          <label className="text-sm font-medium mb-1.5 block text-ink" htmlFor="email">
            Adresse e-mail
          </label>
          <div className="relative">
            <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
            <Input
              id="email"
              type="email"
              placeholder="votre@email.com"
              className="pl-10 h-11"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-ink" htmlFor="password">
              Mot de passe
            </label>
            <Link
              href="/auth/forgot-password"
              className="text-xs text-accent hover:underline font-medium"
            >
              Mot de passe oublié ?
            </Link>
          </div>
          <div className="relative">
            <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-muted" />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="••••••••"
              className="pl-10 pr-11 h-11"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <button
              type="button"
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-ink-muted hover:text-ink transition-colors"
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
              aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
            >
              {showPassword ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
            </button>
          </div>
        </div>

        <Button
          type="submit"
          variant="navy"
          className="w-full h-12 text-sm font-semibold"
          disabled={loading}
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <span className="w-4 h-4 border-2 border-surface-light/40 border-t-surface-light rounded-full animate-spin" />
              Connexion…
            </span>
          ) : (
            'Se connecter'
          )}
        </Button>
      </form>

      <div className="mt-10 pt-6 border-t border-border-subtle text-center">
        <p className="text-sm text-ink-secondary">
          Pas encore de compte ?{' '}
          <Link href={registerHref} className="text-accent font-semibold hover:underline">
            Créer un compte gratuitement
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}

export default function LoginPage() {
  return (
    <Suspense>
      <LoginForm />
    </Suspense>
  );
}
